import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './global-dashboard.css';

const GlobalDashboard = () => {
  const [billsValises, setBillsValises] = useState([]);
  const [billsPin, setBillsPin] = useState([]);
  const [productsValises, setProductsValises] = useState([]);
  const [productsPin, setProductsPin] = useState([]);
  const [invoices, setInvoices] = useState([]); // Factures d'achat
  const [chargesInvoices, setChargesInvoices] = useState([]); // Factures de charges
  const [technicalSheets, setTechnicalSheets] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const [filters, setFilters] = useState({
    startDate: new Date().toISOString().split('T')[0].substring(0, 7) + '-01',
    endDate: new Date().toISOString().split('T')[0],
    location: '',
    supplier: ''
  });

  const extractLocId = (loc) => {
    if (!loc) return null;
    if (typeof loc === "string") return loc;
    if (loc.$oid) return loc.$oid;
    if (loc._id) return loc._id.toString();
    return null;
  };

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem("token");
      
      const headers = token ? { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      } : { 'Content-Type': 'application/json' };
      
      const [
        billsValisesRes, 
        productsValisesRes, 
        invoicesRes,
        locationsRes, 
        chargesRes,
        billsPinRes,
        productsPinRes,
        sheetsRes
      ] = await Promise.all([
        axios.get("/api/BillsValises/getAllBillsV", { headers }),
        axios.get("/api/ProductV/getProductV", { headers }),
        axios.get("/api/InvoicesV/getInvoices?populate=subProductV", { headers }),
        axios.get("/api/Locations/getAllLocations", { headers }),
        axios.get("/api/invoices", { headers }),
        axios.get("/api/Bills/getAllBills", { headers }),
        axios.get("/api/Product/getProduct", { headers }),
        axios.get("/api/TechnicalSheet/all-sheets", { headers })
      ]);

      setBillsValises(billsValisesRes.data || []);
      setProductsValises(productsValisesRes.data || []);
      setInvoices(invoicesRes.data || []);
      setLocations(locationsRes.data || []);
      setChargesInvoices(chargesRes.data || []);
      setBillsPin(billsPinRes.data || []);
      setProductsPin(productsPinRes.data || []);
      setTechnicalSheets(sheetsRes.data || []);

      console.log("📦 Données chargées:", {
        billsValises: billsValisesRes.data?.length || 0,
        billsPin: billsPinRes.data?.length || 0,
        productsValises: productsValisesRes.data?.length || 0,
        productsPin: productsPinRes.data?.length || 0,
        invoices: invoicesRes.data?.length || 0,
        charges: chargesRes.data?.length || 0,
        technicalSheets: sheetsRes.data?.length || 0
      });

    } catch (err) {
      console.error("❌ Erreur chargement données:", err);
      setError('Erreur lors du chargement des données.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // 🔥 Trouver le prix d'achat TTC (Application Valises)
  const findPurchasePrice = useCallback((productId, subProductId) => {
    for (const invoice of invoices) {
      if (!invoice.items || !Array.isArray(invoice.items)) continue;

      for (const item of invoice.items) {
        const itemProductId = item.productId?.toString();
        const itemSubProductId = item.subProductId?.toString();
        const itemProductV = item.productV?._id?.toString() || item.productV?.toString();
        const itemSubProductV = item.subProductV?._id?.toString() || item.subProductV?.toString();

        if (subProductId) {
          const searchSubProductId = subProductId.toString();
          
          if ((itemSubProductId && itemSubProductId === searchSubProductId) ||
              (itemSubProductV && itemSubProductV === searchSubProductId)) {
            
            let purchasePriceTTC;
            if (item.pricettc && item.pricettc > 0) {
              purchasePriceTTC = item.pricettc;
            } else {
              const purchasePriceHT = item.price || 0;
              const tvaRate = item.tva || 0;
              purchasePriceTTC = purchasePriceHT * (1 + tvaRate / 100);
            }
            return purchasePriceTTC || 0;
          }
        }
        
        if (productId) {
          const searchProductId = productId.toString();
          
          if ((itemProductId && itemProductId === searchProductId) ||
              (itemProductV && itemProductV === searchProductId)) {
            
            let purchasePriceTTC;
            if (item.pricettc && item.pricettc > 0) {
              purchasePriceTTC = item.pricettc;
            } else {
              const purchasePriceHT = item.price || 0;
              const tvaRate = item.tva || 0;
              purchasePriceTTC = purchasePriceHT * (1 + tvaRate / 100);
            }
            return purchasePriceTTC || 0;
          }
        }
      }
    }
    return 0;
  }, [invoices]);

  // 🔍 Obtenir les détails d'un produit (Application Valises)
  const getProductDetailsValises = useCallback((item) => {
    const mainProduct = productsValises.find((p) => p._id === item.id);
    
    if (mainProduct) {
      let purchasePriceTTC;
      
      if (mainProduct.category === "SET" && mainProduct.subProductsV?.length > 0) {
        purchasePriceTTC = mainProduct.subProductsV.reduce((total, subProduct) => {
          const subProductPurchasePrice = findPurchasePrice(mainProduct._id, subProduct._id);
          return total + subProductPurchasePrice;
        }, 0);
      } else {
        purchasePriceTTC = findPurchasePrice(mainProduct._id, null);
      }
      
      return {
        _id: mainProduct._id,
        parent: mainProduct.name,
        name: null,
        sellingPrice: item.price || mainProduct.price,
        purchasePrice: purchasePriceTTC,
        localisation: extractLocId(mainProduct.localisation),
        type: 'achat'
      };
    }

    for (const product of productsValises) {
      const subProduct = product.subProductsV?.find((sp) => sp._id === item.id);
      if (subProduct) {
        const purchasePriceTTC = findPurchasePrice(product._id, subProduct._id);
        return {
          _id: subProduct._id,
          parent: product.name,
          name: subProduct.name,
          sellingPrice: item.price || subProduct.price,
          purchasePrice: purchasePriceTTC,
          localisation: extractLocId(subProduct.localisation),
          type: 'achat'
        };
      }
    }

    return {
      _id: item.id,
      name: item.name || "Produit inconnu",
      parent: null,
      sellingPrice: item.price || 0,
      purchasePrice: 0,
      localisation: null,
      type: 'achat'
    };
  }, [productsValises, findPurchasePrice]);

  // 🔍 Trouver le nom d'un produit (Application Pin)
  const findProductNameByIdPin = useCallback((productId) => {
    if (!productId) return "Produit inconnu";

    const productIdStr = extractLocId(productId);

    for (const product of productsPin) {
      if (product.subProducts) {
        const subProduct = product.subProducts.find(sp => 
          extractLocId(sp._id) === productIdStr
        );
        if (subProduct) return subProduct.name;
      }
    }

    const mainProduct = productsPin.find(p => 
      extractLocId(p._id) === productIdStr
    );
    if (mainProduct) return mainProduct.name;

    return "Produit inconnu";
  }, [productsPin]);

  // 🔥 Trouver la localisation d'un produit (Application Pin)
  const findProductLocationPin = useCallback((productId) => {
    if (!productId) return null;

    const productIdStr = extractLocId(productId);

    for (const product of productsPin) {
      if (product.subProducts) {
        const subProduct = product.subProducts.find(sp => 
          extractLocId(sp._id) === productIdStr
        );
        if (subProduct) {
          return extractLocId(subProduct.localisation);
        }
      }
    }

    const mainProduct = productsPin.find(p => 
      extractLocId(p._id) === productIdStr
    );
    if (mainProduct) {
      return extractLocId(mainProduct.localisation);
    }

    return null;
  }, [productsPin]);

  // 🔥 Trouver le coût de production (Application Pin)
  const findProductionCost = useCallback((productId) => {
    if (!productId) return 0;

    const productIdStr = extractLocId(productId);

    const sheet = technicalSheets.find(sheet => 
      extractLocId(sheet.subProduct) === productIdStr ||
      extractLocId(sheet.product) === productIdStr
    );

    if (sheet) {
      return sheet.totalCost || 0;
    }

    let defaultCost = 0;
    for (const product of productsPin) {
      const subProduct = product.subProducts?.find(sp => 
        extractLocId(sp._id) === productIdStr
      );
      if (subProduct && subProduct.price) {
        defaultCost = subProduct.price * 0.7;
        break;
      }
    }

    return defaultCost;
  }, [technicalSheets, productsPin]);

  // 🔍 Obtenir les détails d'un produit (Application Pin)
  const getProductDetailsPin = useCallback((item) => {
    const productName = findProductNameByIdPin(item.id);
    const localisation = findProductLocationPin(item.id);
    const productionCost = findProductionCost(item.id);

    return {
      _id: item.id,
      parent: productName,
      name: null,
      sellingPrice: item.price || 0,
      purchasePrice: productionCost,
      localisation: localisation,
      type: 'production'
    };
  }, [findProductNameByIdPin, findProductLocationPin, findProductionCost]);

  // ==================== CALCUL DES RÉSULTATS GLOBAUX ====================

  const calculateGlobalResult = useCallback(() => {
    console.log('🧮 Calcul des résultats combinés...');
    
    const resultByLocation = {};
    const purchaseByLocation = {};
    const stockValueByLocation = {};
    const soldProductsByLocation = {};
    const chargesByLocation = {};
    
    let totalCA = 0;
    let totalCost = 0;
    let totalPurchases = 0;
    let totalStockValue = 0;
    let totalCharges = 0;
    let totalItemsSold = 0;

    // 1. Initialiser toutes les localisations
    const allLocationIds = new Set();

    // Ajouter les localisations des produits Valises
    productsValises.forEach(product => {
      const locId = extractLocId(product.localisation);
      if (locId) allLocationIds.add(locId);
      
      if (product.subProductsV) {
        product.subProductsV.forEach(subProduct => {
          const subLocId = extractLocId(subProduct.localisation);
          if (subLocId) allLocationIds.add(subLocId);
        });
      }
    });

    // Ajouter les localisations des produits Pin
    productsPin.forEach(product => {
      const locId = extractLocId(product.localisation);
      if (locId) allLocationIds.add(locId);
      
      if (product.subProducts) {
        product.subProducts.forEach(subProduct => {
          const subLocId = extractLocId(subProduct.localisation);
          if (subLocId) allLocationIds.add(subLocId);
        });
      }
    });

    // Ajouter les localisations du système
    locations.forEach(loc => {
      const locId = extractLocId(loc._id);
      if (locId) allLocationIds.add(locId);
    });

    // Initialiser les structures pour toutes les localisations
    allLocationIds.forEach(locId => {
      const locationName = locations.find(l => extractLocId(l._id) === locId)?.name || 
                          `Location ${locId}`;
      
      resultByLocation[locId] = {
        name: locationName,
        ca: 0,
        cost: 0,
        margin: 0,
        percentage: 0,
        charges: 0,
        rbe: 0,
        itemsSold: [],
        salesCount: 0
      };
      
      purchaseByLocation[locId] = {
        name: locationName,
        total: 0,
        items: []
      };
      
      stockValueByLocation[locId] = {
        name: locationName,
        value: 0,
        items: [],
        purchasesValue: 0,
        soldValue: 0,
        quantitySold: 0,
        itemsCount: 0
      };
      
      soldProductsByLocation[locId] = {
        name: locationName,
        items: new Map()
      };
      
      chargesByLocation[locId] = {
        name: locationName,
        total: 0,
        items: [],
        count: 0,
        details: []
      };
    });
    
    // Ajouter une entrée pour 'unknown'
    resultByLocation['unknown'] = {
      name: 'Non attribué',
      ca: 0,
      cost: 0,
      margin: 0,
      percentage: 0,
      charges: 0,
      rbe: 0,
      itemsSold: [],
      salesCount: 0
    };
    purchaseByLocation['unknown'] = {
      name: 'Non attribué',
      total: 0,
      items: []
    };
    stockValueByLocation['unknown'] = {
      name: 'Non attribué',
      value: 0,
      items: [],
      purchasesValue: 0,
      soldValue: 0,
      quantitySold: 0,
      itemsCount: 0
    };
    soldProductsByLocation['unknown'] = {
      name: 'Non attribué',
      items: new Map()
    };
    chargesByLocation['unknown'] = {
      name: 'Non attribué',
      total: 0,
      items: [],
      count: 0,
      details: []
    };

    // 2. Calculer le TOTAL DES ACHATS par localisation
    const filteredInvoices = invoices.filter(inv => {
      if (!inv.date) return false;
      const invDate = new Date(inv.date);
      
      if (filters.startDate && invDate < new Date(filters.startDate)) return false;
      if (filters.endDate && invDate > new Date(filters.endDate)) return false;
      if (filters.supplier && inv.supplierName !== filters.supplier) return false;
      return true;
    });

    console.log(`📦 Factures d'achat: ${filteredInvoices.length}`);

    filteredInvoices.forEach(invoice => {
      let invoiceLocation = null;
      let invoiceTotal = 0;
      const invoiceItems = [];

      if (invoice.items && Array.isArray(invoice.items)) {
        invoice.items.forEach(item => {
          let itemCost = 0;
          if (item.pricettc && item.pricettc > 0) {
            itemCost = item.pricettc;
          } else {
            const purchasePriceHT = item.price || 0;
            const tvaRate = item.tva || 0;
            itemCost = purchasePriceHT * (1 + tvaRate / 100);
          }
          
          invoiceTotal += itemCost;
          invoiceItems.push({
            productId: item.productId || item.productV,
            subProductId: item.subProductId || item.subProductV,
            cost: itemCost,
            quantity: item.quantity || 1
          });
          
          if (!invoiceLocation) {
            const productId = item.productId || item.productV;
            const product = productsValises.find(p => p._id === productId);
            if (product) {
              invoiceLocation = extractLocId(product.localisation);
            }
          }
        });
      } else {
        invoiceTotal = invoice.totalTTC || 0;
        invoiceLocation = invoice.location || null;
      }

      if (filters.location && invoiceLocation !== filters.location) {
        return;
      }

      const locationId = invoiceLocation || 'unknown';
      if (!purchaseByLocation[locationId]) {
        purchaseByLocation[locationId] = {
          name: locationId === 'unknown' ? 'Non attribué' : `Location ${locationId}`,
          total: 0,
          items: []
        };
      }
      
      purchaseByLocation[locationId].total += invoiceTotal;
      purchaseByLocation[locationId].items.push(...invoiceItems);
      totalPurchases += invoiceTotal;
    });

    // 3. Calculer le CA et les COÛTS (Application Valises)
    const filteredBillsValises = billsValises.filter(bill => {
      if (!bill.date) return false;
      const billDate = new Date(bill.date);
      
      if (filters.startDate && billDate < new Date(filters.startDate)) return false;
      if (filters.endDate && billDate > new Date(filters.endDate)) return false;
      return true;
    });

    console.log(`🛒 Factures Valises: ${filteredBillsValises.length}`);

    filteredBillsValises.forEach(bill => {
      let billTotal = 0;
      let billLocation = null;
      const billProducts = [];

      if (bill.items && Array.isArray(bill.items)) {
        bill.items.forEach(item => {
          const details = getProductDetailsValises(item);
          const quantity = item.quantity || 1;
          const salesAmount = details.sellingPrice * quantity;
          const costAmount = details.purchasePrice * quantity;
          
          billTotal += salesAmount;
          totalItemsSold += quantity;
          
          if (!billLocation) {
            billLocation = extractLocId(details.localisation);
          }

          const locationId = billLocation || 'unknown';
          if (!soldProductsByLocation[locationId]) {
            soldProductsByLocation[locationId] = {
              name: locationId === 'unknown' ? 'Non attribué' : `Location ${locationId}`,
              items: new Map()
            };
          }
          
          const productKey = details._id || item.id;
          const current = soldProductsByLocation[locationId].items.get(productKey) || {
            id: details._id,
            name: details.parent || details.name,
            purchasePrice: details.purchasePrice,
            quantitySold: 0,
            totalCost: 0,
            type: details.type
          };
          
          current.quantitySold += quantity;
          current.totalCost += costAmount;
          soldProductsByLocation[locationId].items.set(productKey, current);

          billProducts.push({
            id: details._id,
            name: details.parent || details.name,
            quantity: quantity,
            sellingPrice: details.sellingPrice,
            purchasePrice: details.purchasePrice,
            salesAmount: salesAmount,
            costAmount: costAmount,
            type: details.type
          });
        });
      } else {
        billTotal = bill.totalTTC || 0;
        billLocation = bill.location || null;
      }

      if (filters.location && billLocation !== filters.location) {
        return;
      }

      const locationId = billLocation || 'unknown';
      if (!resultByLocation[locationId]) {
        resultByLocation[locationId] = {
          name: locationId === 'unknown' ? 'Non attribué' : `Location ${locationId}`,
          ca: 0,
          cost: 0,
          margin: 0,
          percentage: 0,
          charges: 0,
          rbe: 0,
          itemsSold: [],
          salesCount: 0
        };
      }
      
      let billCost = 0;
      billProducts.forEach(product => {
        billCost += product.costAmount;
      });
      
      resultByLocation[locationId].ca += billTotal;
      resultByLocation[locationId].cost += billCost;
      resultByLocation[locationId].itemsSold.push(...billProducts);
      resultByLocation[locationId].salesCount += 1;
      
      totalCA += billTotal;
      totalCost += billCost;
    });

    // 4. Calculer le CA et les COÛTS (Application Pin)
    const filteredBillsPin = billsPin.filter(bill => {
      if (!bill.date) return false;
      const billDate = new Date(bill.date);
      
      if (filters.startDate && billDate < new Date(filters.startDate)) return false;
      if (filters.endDate && billDate > new Date(filters.endDate)) return false;
      return true;
    });

    console.log(`🏭 Factures Pin: ${filteredBillsPin.length}`);

    filteredBillsPin.forEach(bill => {
      let billTotal = 0;
      let billLocation = null;
      const billProducts = [];

      if (bill.items && Array.isArray(bill.items)) {
        bill.items.forEach(item => {
          const details = getProductDetailsPin(item);
          const quantity = item.quantity || 1;
          const salesAmount = details.sellingPrice * quantity;
          const costAmount = details.purchasePrice * quantity;
          
          billTotal += salesAmount;
          totalItemsSold += quantity;
          
          if (!billLocation) {
            billLocation = extractLocId(details.localisation);
          }

          const locationId = billLocation || 'unknown';
          if (!soldProductsByLocation[locationId]) {
            soldProductsByLocation[locationId] = {
              name: locationId === 'unknown' ? 'Non attribué' : `Location ${locationId}`,
              items: new Map()
            };
          }
          
          const productKey = details._id || item.id;
          const current = soldProductsByLocation[locationId].items.get(productKey) || {
            id: details._id,
            name: details.parent || details.name,
            purchasePrice: details.purchasePrice,
            quantitySold: 0,
            totalCost: 0,
            type: details.type
          };
          
          current.quantitySold += quantity;
          current.totalCost += costAmount;
          soldProductsByLocation[locationId].items.set(productKey, current);

          billProducts.push({
            id: details._id,
            name: details.parent || details.name,
            quantity: quantity,
            sellingPrice: details.sellingPrice,
            purchasePrice: details.purchasePrice,
            salesAmount: salesAmount,
            costAmount: costAmount,
            type: details.type
          });
        });
      } else {
        billTotal = bill.totalTTC || 0;
        billLocation = bill.location || null;
      }

      if (filters.location && billLocation !== filters.location) {
        return;
      }

      const locationId = billLocation || 'unknown';
      if (!resultByLocation[locationId]) {
        resultByLocation[locationId] = {
          name: locationId === 'unknown' ? 'Non attribué' : `Location ${locationId}`,
          ca: 0,
          cost: 0,
          margin: 0,
          percentage: 0,
          charges: 0,
          rbe: 0,
          itemsSold: [],
          salesCount: 0
        };
      }
      
      let billCost = 0;
      billProducts.forEach(product => {
        billCost += product.costAmount;
      });
      
      resultByLocation[locationId].ca += billTotal;
      resultByLocation[locationId].cost += billCost;
      resultByLocation[locationId].itemsSold.push(...billProducts);
      resultByLocation[locationId].salesCount += 1;
      
      totalCA += billTotal;
      totalCost += billCost;
    });

    // 5. Calculer la VALEUR DU STOCK par localisation
    Object.keys(purchaseByLocation).forEach(locId => {
      const purchases = purchaseByLocation[locId];
      const soldProducts = soldProductsByLocation[locId];
      
      if (!stockValueByLocation[locId]) {
        stockValueByLocation[locId] = {
          name: locId === 'unknown' ? 'Non attribué' : `Location ${locId}`,
          value: 0,
          items: [],
          purchasesValue: 0,
          soldValue: 0,
          quantitySold: 0,
          itemsCount: 0
        };
      }
      
      const stockLocation = stockValueByLocation[locId];
      
      const totalPurchasesInLocation = purchases?.total || 0;
      let totalCostSoldInLocation = 0;
      let totalQuantitySold = 0;
      
      if (soldProducts && soldProducts.items) {
        soldProducts.items.forEach((product, key) => {
          totalCostSoldInLocation += product.totalCost || 0;
          totalQuantitySold += product.quantitySold || 0;
        });
      }
      
      const stockValue = totalPurchasesInLocation - totalCostSoldInLocation;
      
      if (stockLocation) {
        stockLocation.value = Math.max(0, stockValue);
        stockLocation.purchasesValue = totalPurchasesInLocation;
        stockLocation.soldValue = totalCostSoldInLocation;
        stockLocation.quantitySold = totalQuantitySold;
        stockLocation.itemsCount = purchases?.items?.length || 0;
        stockLocation.name = purchases?.name || stockLocation.name;
        
        totalStockValue += stockLocation.value;
      }
    });

    // 6. Calculer les CHARGES
    const filteredCharges = chargesInvoices.filter(charge => {
      if (!charge.date) return false;
      const chargeDate = new Date(charge.date);
      
      if (filters.startDate && chargeDate < new Date(filters.startDate)) return false;
      if (filters.endDate && chargeDate > new Date(filters.endDate)) return false;
      return true;
    });

    console.log(`💸 Factures de charges: ${filteredCharges.length}`);
    
    filteredCharges.forEach(charge => {
      const chargeAmount = charge.totalTTC || 0;
      
      if (chargeAmount <= 0) {
        return;
      }
      
      const chargeLocation = charge.location ? extractLocId(charge.location) : 'unknown';
      
      if (filters.location && chargeLocation !== filters.location) {
        return;
      }

      const locationId = chargeLocation;
      
      if (!chargesByLocation[locationId]) {
        chargesByLocation[locationId] = {
          name: locationId === 'unknown' ? 'Non attribué' : 
                locations.find(l => extractLocId(l._id) === locationId)?.name || 
                resultByLocation[locationId]?.name || 
                `Location ${locationId}`,
          total: 0,
          items: [],
          count: 0,
          details: []
        };
      }
      
      chargesByLocation[locationId].total += chargeAmount;
      chargesByLocation[locationId].items.push(charge);
      chargesByLocation[locationId].count += 1;
      chargesByLocation[locationId].details.push({
        id: charge._id || charge.id,
        date: charge.date,
        supplierName: charge.supplierName || 'Fournisseur inconnu',
        invoiceNumber: charge.invoiceNumber || 'Sans numéro',
        amount: chargeAmount,
        type: 'charge'
      });
      
      if (!resultByLocation[locationId]) {
        resultByLocation[locationId] = {
          name: locationId === 'unknown' ? 'Non attribué' : 
                locations.find(l => extractLocId(l._id) === locationId)?.name || 
                `Location ${locationId}`,
          ca: 0,
          cost: 0,
          margin: 0,
          percentage: 0,
          charges: 0,
          rbe: 0,
          itemsSold: [],
          salesCount: 0
        };
      }
      
      resultByLocation[locationId].charges += chargeAmount;
      totalCharges += chargeAmount;
    });

    // 7. Calculer les marges et RBE par localisation
    Object.keys(resultByLocation).forEach(locId => {
      const locData = resultByLocation[locId];
      if (locData) {
        locData.margin = locData.ca - locData.cost;
        locData.percentage = locData.ca > 0 ? (locData.margin / locData.ca) * 100 : 0;
        locData.rbe = locData.margin - locData.charges;
      }
    });

    // 8. Calculer le RBE global
    const totalMargin = totalCA - totalCost;
    const rbe = totalMargin - totalCharges;

    // 9. Compter le nombre total d'articles achetés
    const totalItemsPurchased = filteredInvoices.reduce((total, invoice) => {
      if (invoice.items && Array.isArray(invoice.items)) {
        return total + invoice.items.reduce((sum, item) => sum + (item.quantity || 1), 0);
      }
      return total;
    }, 0);

    // 10. Calculer le nombre total de ventes combinées
    const totalSalesCount = filteredBillsValises.length + filteredBillsPin.length;

    console.log('📈 Résultats combinés calculés:', {
      totalCA,
      totalCost,
      totalPurchases,
      totalStockValue,
      totalItemsPurchased,
      totalItemsSold,
      totalCharges,
      rbe,
      salesCount: totalSalesCount,
      locationsCount: Object.keys(resultByLocation).length,
      chargesByLocation: Object.keys(chargesByLocation).length
    });

    return {
      byLocation: resultByLocation,
      purchasesByLocation: purchaseByLocation,
      stockByLocation: stockValueByLocation,
      soldByLocation: soldProductsByLocation,
      chargesByLocation: chargesByLocation,
      totals: {
        ca: totalCA,
        cost: totalCost,
        purchases: totalPurchases,
        margin: totalMargin,
        charges: totalCharges,
        stockValue: totalStockValue,
        itemsPurchased: totalItemsPurchased,
        itemsSold: totalItemsSold,
        rbe: rbe,
        salesCount: totalSalesCount,
        marginPercentage: totalCA > 0 ? (totalMargin / totalCA) * 100 : 0,
        rbePercentage: totalCA > 0 ? (rbe / totalCA) * 100 : 0
      },
      counts: {
        billsValises: filteredBillsValises.length,
        billsPin: filteredBillsPin.length,
        invoices: filteredInvoices.length,
        charges: filteredCharges.length,
        locations: locations.length
      }
    };
  }, [
    billsValises, 
    billsPin, 
    productsValises, 
    productsPin, 
    invoices, 
    chargesInvoices,
    locations, 
    filters, 
    technicalSheets,
    findPurchasePrice,
    getProductDetailsValises,
    findProductNameByIdPin,
    findProductLocationPin,
    findProductionCost,
    getProductDetailsPin
  ]);

  const globalResult = calculateGlobalResult();

  const handleRefresh = () => {
    fetchAllData();
  };

  const formatNumber = (number) => {
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(number || 0);
  };

  const formatInteger = (number) => {
    return new Intl.NumberFormat('fr-FR').format(number || 0);
  };

  const getValueStyle = (value) => {
    return (value || 0) >= 0 ? 'value-positive' : 'value-negative';
  };

  const handleResetFilters = () => {
    const today = new Date().toISOString().split('T')[0];
    const firstDayOfMonth = today.substring(0, 7) + '-01';
    
    setFilters({
      startDate: firstDayOfMonth,
      endDate: today,
      location: '',
      supplier: ''
    });
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Chargement des données...</p>
      </div>
    );
  }

  return (
    <div className="global-dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <h1 className="dashboard-title">📊 Tableau de Bord Global</h1>
        <p className="dashboard-subtitle">État de résultat combiné (Valises + Pin)</p>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="alert alert-danger">
          <strong>❌ Erreur</strong>
          <p>{error}</p>
        </div>
      )}

      {/* Filters */}
      <div className="filters-container">
        <div className="filters-header">
          <h3 className="filters-title">Filtres</h3>
          <div className="filter-actions">
            <button className="action-btn btn-reset" onClick={handleResetFilters}>
              <span>🔄</span>
              Réinitialiser
            </button>
            <button className="action-btn btn-refresh" onClick={handleRefresh}>
              <span>⚡</span>
              Actualiser
            </button>
          </div>
        </div>
        
        <div className="filters-grid">
          <div className="filter-group">
            <label className="filter-label">📅 Date de début</label>
            <input
              type="date"
              className="filter-input"
              value={filters.startDate}
              onChange={(e) => setFilters({...filters, startDate: e.target.value})}
            />
          </div>
          
          <div className="filter-group">
            <label className="filter-label">📅 Date de fin</label>
            <input
              type="date"
              className="filter-input"
              value={filters.endDate}
              onChange={(e) => setFilters({...filters, endDate: e.target.value})}
            />
          </div>
          
          <div className="filter-group">
            <label className="filter-label">📍 Localisation</label>
            <select
              className="filter-select"
              value={filters.location}
              onChange={(e) => setFilters({...filters, location: e.target.value})}
            >
              <option value="">Toutes les localisations</option>
              {locations.map(loc => {
                const locId = extractLocId(loc._id);
                return (
                  <option key={locId} value={locId}>
                    {loc.name}
                  </option>
                );
              })}
            </select>
          </div>
        </div>
      </div>

      {/* Stats Grid - Principales métriques */}
      <div className="stats-grid">
        <div className="stat-card ca-card">
          <div className="stat-icon">💰</div>
          <h4 className="stat-title">Chiffre d'Affaires</h4>
          <div className="stat-value">{formatNumber(globalResult.totals.ca)} DT</div>
          <p className="stat-subtitle">
            {globalResult.totals.salesCount} ventes
            <br />
            <small>📦 {globalResult.counts.billsValises} Valises</small>
            <br />
            <small>🏭 {globalResult.counts.billsPin} Pin</small>
          </p>
        </div>
        
        <div className="stat-card cost-card">
          <div className="stat-icon">📦</div>
          <h4 className="stat-title">Coût des Ventes</h4>
          <div className="stat-value">{formatNumber(globalResult.totals.cost)} DT</div>
          <p className="stat-subtitle">{formatInteger(globalResult.totals.itemsSold)} articles vendus</p>
        </div>
        
        <div className="stat-card margin-card">
          <div className="stat-icon">📈</div>
          <h4 className="stat-title">Marge Brute</h4>
          <div className={`stat-value ${getValueStyle(globalResult.totals.margin)}`}>
            {formatNumber(globalResult.totals.margin)} DT
          </div>
          <p className="stat-subtitle">{globalResult.totals.marginPercentage.toFixed(1)}%</p>
        </div>
        
        <div className="stat-card rbe-card">
          <div className="stat-icon">🏆</div>
          <h4 className="stat-title">RBE</h4>
          <div className={`stat-value ${getValueStyle(globalResult.totals.rbe)}`}>
            {formatNumber(globalResult.totals.rbe)} DT
          </div>
          <p className="stat-subtitle">Résultat Brut d'Exploitation</p>
        </div>
      </div>

      {/* Statistics Cards - Métriques secondaires */}
      <div className="stats-grid secondary-stats">
        <div className="stat-card purchases-card">
          <div className="stat-icon">🛒</div>
          <h4 className="stat-title">Achats Totaux</h4>
          <div className="stat-value">{formatNumber(globalResult.totals.purchases)} DT</div>
          <p className="stat-subtitle">{globalResult.counts.invoices} factures d'achat</p>
        </div>
        
        <div className="stat-card stock-card">
          <div className="stat-icon">📊</div>
          <h4 className="stat-title">Valeur Stock</h4>
          <div className="stat-value value-positive">{formatNumber(globalResult.totals.stockValue)} DT</div>
          <p className="stat-subtitle">{formatInteger(globalResult.totals.itemsPurchased - globalResult.totals.itemsSold)} articles</p>
        </div>
        
        <div className="stat-card charges-card">
          <div className="stat-icon">💸</div>
          <h4 className="stat-title">Charges</h4>
          <div className="stat-value value-negative">{formatNumber(globalResult.totals.charges)} DT</div>
          <p className="stat-subtitle">{globalResult.counts.charges} factures de charges</p>
        </div>
        
        <div className="stat-card efficiency-card">
          <div className="stat-icon">⚡</div>
          <h4 className="stat-title">Taux de Rotation</h4>
          <div className="stat-value">
            {globalResult.totals.itemsPurchased > 0 
              ? ((globalResult.totals.itemsSold / globalResult.totals.itemsPurchased) * 100).toFixed(1)
              : '0'}%
          </div>
          <p className="stat-subtitle">Articles vendus/achetés</p>
        </div>
      </div>

      {/* Main Results Table */}
      <div className="dashboard-card">
        <div className="card-header">
          <h2 className="card-title">
            <span>📋</span>
            État de Résultat Combiné par Localisation
          </h2>
          <div className="table-summary">
            <span className="badge badge-primary">
              {Object.entries(globalResult.byLocation).filter(([_, data]) => data && (data.ca > 0 || data.cost > 0 || data.charges > 0)).length} localisations actives
            </span>
          </div>
        </div>
        
        <div className="table-responsive">
          <table className="enhanced-table">
            <thead>
              <tr>
                <th>Localisation</th>
                <th className="text-end">Ventes</th>
                <th className="text-end">CA (TTC)</th>
                <th className="text-end">Coût Ventes</th>
                <th className="text-end">Marge Brute</th>
                <th className="text-end">% Marge</th>
                <th className="text-end">Charges</th>
                <th className="text-end">RBE</th>
                <th className="text-end">% RBE</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(globalResult.byLocation)
                .filter(([_, data]) => data && (data.ca > 0 || data.cost > 0 || data.charges > 0))
                .sort(([_, a], [__, b]) => (b.ca || 0) - (a.ca || 0)) // Trier par CA décroissant
                .map(([locId, data]) => {
                  const rbePercentage = data.ca > 0 ? (data.rbe / data.ca) * 100 : 0;
                  return (
                    <tr key={locId} className="location-row">
                      <td>
                        <div className="location-info">
                          <strong>{data?.name || 'Inconnu'}</strong>
                          <small className="text-muted d-block">
                            {data?.itemsSold?.length || 0} produits vendus
                          </small>
                        </div>
                      </td>
                      <td className="text-end">
                        {data.salesCount || 0}
                      </td>
                      <td className="text-end">{formatNumber(data?.ca)} DT</td>
                      <td className="text-end">{formatNumber(data?.cost)} DT</td>
                      <td className={`text-end fw-bold ${getValueStyle(data?.margin)}`}>
                        {formatNumber(data?.margin)} DT
                      </td>
                      <td className={`text-end ${getValueStyle(data?.percentage)}`}>
                        {(data?.percentage || 0).toFixed(1)}%
                      </td>
                      <td className={`text-end value-negative`}>
                        -{formatNumber(data?.charges || 0)} DT
                      </td>
                      <td className={`text-end fw-bold ${getValueStyle(data?.rbe)}`}>
                        {formatNumber(data?.rbe)} DT
                      </td>
                      <td className={`text-end ${getValueStyle(rbePercentage)}`}>
                        {rbePercentage.toFixed(1)}%
                      </td>
                    </tr>
                  );
                })}
              
              {/* Total Global */}
              <tr className="total-global-row">
                <td>
                  <strong>🏆 TOTAL GLOBAL</strong>
                  <div className="total-subtitle">
                    {filters.location ? 'Location sélectionnée' : 'Toutes localisations'}
                  </div>
                </td>
                <td className="text-end">
                  <strong>{globalResult.totals.salesCount}</strong>
                </td>
                <td className="text-end">
                  <strong>{formatNumber(globalResult.totals.ca)} DT</strong>
                </td>
                <td className="text-end">
                  <strong>{formatNumber(globalResult.totals.cost)} DT</strong>
                </td>
                <td className={`text-end ${getValueStyle(globalResult.totals.margin)}`}>
                  <strong>{formatNumber(globalResult.totals.margin)} DT</strong>
                </td>
                <td className={`text-end ${getValueStyle(globalResult.totals.marginPercentage)}`}>
                  <strong>{globalResult.totals.marginPercentage.toFixed(1)}%</strong>
                </td>
                <td className="text-end value-negative">
                  <strong>-{formatNumber(globalResult.totals.charges)} DT</strong>
                </td>
                <td className={`text-end ${getValueStyle(globalResult.totals.rbe)}`}>
                  <strong>{formatNumber(globalResult.totals.rbe)} DT</strong>
                </td>
                <td className={`text-end ${getValueStyle(globalResult.totals.rbePercentage)}`}>
                  <strong>{globalResult.totals.rbePercentage.toFixed(1)}%</strong>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Tableau détaillé des charges */}
      <div className="dashboard-card">
        <div className="card-header">
          <h2 className="card-title">
            <span>💸</span>
            Détail des Charges par Localisation
          </h2>
          <div className="table-summary">
            <span className="badge badge-warning">
              Total: {formatNumber(globalResult.totals.charges)} DT ({globalResult.counts.charges} factures)
            </span>
          </div>
        </div>
        
        <div className="table-responsive">
          <table className="enhanced-table charges-table">
            <thead>
              <tr>
                <th>Localisation</th>
                <th className="text-end">Nombre de charges</th>
                <th className="text-end">Montant total</th>
                <th className="text-end">% du total</th>
                <th className="text-end">Moyenne par charge</th>
                <th>Exemples de fournisseurs</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(globalResult.chargesByLocation)
                .filter(([_, data]) => data && data.total > 0)
                .sort(([_, a], [__, b]) => (b.total || 0) - (a.total || 0)) // Trier par montant décroissant
                .map(([locId, data]) => {
                  const percentage = globalResult.totals.charges > 0 
                    ? (data.total / globalResult.totals.charges) * 100 
                    : 0;
                  const average = data.count > 0 ? data.total / data.count : 0;
                  
                  return (
                    <tr key={locId} className="charges-row">
                      <td>
                        <div className="location-info">
                          <strong>{data?.name || 'Inconnu'}</strong>
                        </div>
                      </td>
                      <td className="text-end">
                        <span className="badge bg-warning">
                          {data.count} facture{data.count !== 1 ? 's' : ''}
                        </span>
                      </td>
                      <td className="text-end value-negative fw-bold">
                        -{formatNumber(data.total)} DT
                      </td>
                      <td className="text-end">
                        <div className="percentage-bar">
                          <div 
                            className="percentage-fill"
                            style={{ width: `${Math.min(percentage, 100)}%` }}
                          ></div>
                          <span className="percentage-text">{percentage.toFixed(1)}%</span>
                        </div>
                      </td>
                      <td className="text-end">
                        {formatNumber(average)} DT
                      </td>
                      <td>
                        <div className="charges-details">
                          {data.details && data.details.slice(0, 2).map((detail, idx) => (
                            <div key={idx} className="charge-detail-item">
                              <small className="text-muted">
                                {detail.supplierName}: {formatNumber(detail.amount)} DT
                              </small>
                            </div>
                          ))}
                          {data.details && data.details.length > 2 && (
                            <small className="text-muted">
                              ... et {data.details.length - 2} autre{data.details.length - 2 > 1 ? 's' : ''}
                            </small>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              
              {/* Total des charges */}
              {globalResult.totals.charges > 0 ? (
                <tr className="total-charges-row">
                  <td>
                    <strong>📊 TOTAL DES CHARGES</strong>
                  </td>
                  <td className="text-end">
                    <strong>
                      <span className="badge bg-danger">
                        {globalResult.counts.charges} facture{globalResult.counts.charges !== 1 ? 's' : ''}
                      </span>
                    </strong>
                  </td>
                  <td className="text-end value-negative">
                    <strong>-{formatNumber(globalResult.totals.charges)} DT</strong>
                  </td>
                  <td className="text-end">
                    <strong>100%</strong>
                  </td>
                  <td className="text-end">
                    <strong>
                      {globalResult.counts.charges > 0 
                        ? formatNumber(globalResult.totals.charges / globalResult.counts.charges)
                        : '0'} DT
                    </strong>
                  </td>
                  <td>
                    <small className="text-muted">Somme de toutes les charges</small>
                  </td>
                </tr>
              ) : (
                <tr>
                  <td colSpan="6" className="text-center text-muted">
                    Aucune charge enregistrée pour la période sélectionnée
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Stock Value Table */}
      <div className="dashboard-card">
        <div className="card-header">
          <h2 className="card-title">
            <span>📦</span>
            Analyse du Stock par Localisation
          </h2>
          <div className="table-summary">
            <span className="badge badge-info">
              Formule: Stock = Achats totaux - Coût des ventes
            </span>
          </div>
        </div>
        
        <div className="table-responsive">
          <table className="enhanced-table stock-table">
            <thead>
              <tr>
                <th>Localisation</th>
                <th className="text-end">Achats Totaux</th>
                <th className="text-end">Coût des Ventes</th>
                <th className="text-end">Valeur du Stock</th>
                <th className="text-end">Rotation</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(globalResult.stockByLocation)
                .filter(([_, data]) => data && (data.purchasesValue > 0 || data.value > 0))
                .sort(([_, a], [__, b]) => (b.value || 0) - (a.value || 0)) // Trier par valeur décroissante
                .map(([locId, data]) => {
                  const rotationRate = data?.purchasesValue > 0 
                    ? ((data?.soldValue || 0) / data.purchasesValue) * 100 
                    : 0;
                  
                  return (
                    <tr key={locId} className="stock-row">
                      <td>
                        <div className="location-info">
                          <strong>{data?.name || 'Inconnu'}</strong>
                          <small className="text-muted d-block">
                            {data?.itemsCount || 0} articles achetés
                          </small>
                        </div>
                      </td>
                      <td className="text-end">
                        {formatNumber(data?.purchasesValue)} DT
                      </td>
                      <td className="text-end">
                        {formatNumber(data?.soldValue)} DT
                      </td>
                      <td className="text-end value-positive">
                        <strong>{formatNumber(data?.value)} DT</strong>
                      </td>
                      <td className="text-end">
                        <div className={`rotation-indicator ${rotationRate >= 50 ? 'high-rotation' : 'low-rotation'}`}>
                          {rotationRate.toFixed(1)}%
                        </div>
                      </td>
                    </tr>
                  );
                })}
              
              {/* Total Stock */}
              <tr className="total-stock-row">
                <td>
                  <strong>📊 TOTAL GLOBAL DU STOCK</strong>
                </td>
                <td className="text-end">
                  <strong>{formatNumber(globalResult.totals.purchases)} DT</strong>
                </td>
                <td className="text-end">
                  <strong>{formatNumber(globalResult.totals.cost)} DT</strong>
                </td>
                <td className="text-end value-positive">
                  <strong>{formatNumber(globalResult.totals.stockValue)} DT</strong>
                </td>
                <td className="text-end">
                  <strong>
                    {globalResult.totals.purchases > 0 
                      ? ((globalResult.totals.cost / globalResult.totals.purchases) * 100).toFixed(1)
                      : '0'}%
                  </strong>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default GlobalDashboard;